/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Full profile MODAL content (Figma 4136:14975) — opened from the popover's
// name tap. Reuses the shared header block (avatar 120px, identity, status,
// actions) then adds: a bio card (elevated surface), a company row with an
// optional "View Company →" link, a "Contact information" section (email +
// phone), and a "Mutual Chats" list. Purely presentational; rendered inside the
// app's centered `Modal` host. Tailwind-first throughout.

import { useState } from 'react';
import { Linking, Pressable, ScrollView, Text, View } from 'react-native';

import { VerifiedBadge } from '../components/VerifiedBadge';
import { ProfileMoreMenu, type ProfileMoreMenuProps } from './ProfileMoreMenu';
import { ChevronRight, ICON_MUTED, ProfileAvatar, ProfileHeaderBlock } from './ProfileParts';
import type { ProfileActions, ProfileView } from './profileViewTypes';

export interface ProfileModalProps {
  readonly view: ProfileView;
  readonly actions: ProfileActions;
  /** ⋯ "More" menu (View full profile / Add to group / Block). */
  readonly moreMenu?: ProfileMoreMenuProps | undefined;
}

/** Section heading ("Contact information" / "Mutual Chats"). */
function SectionLabel({ children }: { children: string }): JSX.Element {
  return (
    <Text className="text-[14px] leading-[20px] text-light-text-tertiary dark:text-dark-text-tertiary">
      {children}
    </Text>
  );
}

export function ProfileModalContent({ view, actions, moreMenu }: ProfileModalProps): JSX.Element {
  const hasContact =
    (view.email !== undefined && view.email.length > 0) ||
    (view.phone !== undefined && view.phone.length > 0);
  const mutuals = view.mutualChats ?? [];

  const [moreOpen, setMoreOpen] = useState(false);
  const effectiveActions: ProfileActions =
    moreMenu !== undefined ? { ...actions, onMore: () => setMoreOpen((o) => !o) } : actions;

  return (
    <View className="relative">
      <ScrollView
        accessibilityLabel={`Profile for ${view.name}`}
        className="bg-light-surface-panel dark:bg-dark-surface-panel"
        contentContainerStyle={{ padding: 16 }}
        showsVerticalScrollIndicator={false}
      >
      <View className="gap-4">
        <ProfileHeaderBlock
          view={view}
          actions={effectiveActions}
          avatarSizeClassName="w-[120px] h-[120px] rounded-[12px]"
        />

        {/* Bio card */}
        {view.bio !== undefined && view.bio.length > 0 ? (
          <View className="rounded-lg bg-light-surface-elevated dark:bg-dark-surface-elevated px-3 py-2">
            <Text className="text-[14px] leading-[20px] text-light-text-primary dark:text-dark-text-primary">
              {view.bio}
            </Text>
          </View>
        ) : null}

        {/* Company row */}
        {view.company !== undefined && view.company.name.length > 0 ? (
          <View className="flex-row items-center gap-2">
            <ProfileAvatar
              name={view.company.name}
              uri={view.company.logoUri}
              sizeClassName="w-[32px] h-[32px] rounded-[6px]"
            />
            <View className="flex-row items-center gap-1 flex-1">
              <Text
                className="text-[16px] leading-[22px] text-light-text-primary dark:text-dark-text-primary"
                numberOfLines={1}
              >
                {view.company.name}
              </Text>
              {view.company.verified === true ? <VerifiedBadge verified size={14} /> : null}
            </View>
            {view.company.onViewCompany !== undefined ? (
              <Pressable
                accessibilityRole="link"
                accessibilityLabel={`View ${view.company.name}`}
                onPress={view.company.onViewCompany}
                className="flex-row items-center gap-1 active:opacity-70"
              >
                <Text className="text-[14px] leading-[20px] text-light-accent-brand dark:text-dark-accent-brand">
                  View Company
                </Text>
                <ChevronRight size={14} color={ICON_MUTED} />
              </Pressable>
            ) : null}
          </View>
        ) : null}

        {/* Contact information */}
        {hasContact ? (
          <View className="gap-2">
            <SectionLabel>Contact information</SectionLabel>
            {view.email !== undefined && view.email.length > 0 ? (
              <Pressable
                accessibilityRole="link"
                accessibilityLabel={`Email ${view.email}`}
                onPress={() => {
                  void Linking.openURL(`mailto:${view.email}`).catch(() => undefined);
                }}
              >
                <Text
                  className="text-[16px] leading-[22px] text-light-accent-brand dark:text-dark-accent-brand"
                  numberOfLines={1}
                >
                  {view.email}
                </Text>
              </Pressable>
            ) : null}
            {view.phone !== undefined && view.phone.length > 0 ? (
              <Pressable
                accessibilityRole="link"
                accessibilityLabel={`Call ${view.phone}`}
                onPress={() => {
                  void Linking.openURL(`tel:${(view.phone ?? '').replace(/[^+\d]/g, '')}`).catch(
                    () => undefined,
                  );
                }}
              >
                <Text
                  className="text-[16px] leading-[22px] text-light-accent-brand dark:text-dark-accent-brand"
                  numberOfLines={1}
                >
                  {view.phone}
                </Text>
              </Pressable>
            ) : null}
          </View>
        ) : null}

        {/* Mutual Chats */}
        {mutuals.length > 0 ? (
          <View className="gap-2">
            <SectionLabel>Mutual Chats</SectionLabel>
            <View className="gap-2">
              {mutuals.map((chat) => {
                const inner = (
                  <>
                    <ProfileAvatar
                      name={chat.name}
                      uri={chat.avatarUri}
                      sizeClassName="w-[42px] h-[42px] rounded-[10px]"
                    />
                    <View className="flex-1">
                      <View className="flex-row items-center gap-1">
                        <Text
                          className="text-[16px] font-bold leading-[22px] text-light-text-secondary dark:text-dark-text-secondary"
                          numberOfLines={1}
                        >
                          {chat.name}
                        </Text>
                        {chat.verified === true ? <VerifiedBadge verified size={14} /> : null}
                      </View>
                      {chat.subtitle !== undefined && chat.subtitle.length > 0 ? (
                        <Text
                          className="text-[14px] leading-[20px] text-light-text-tertiary dark:text-dark-text-tertiary"
                          numberOfLines={1}
                        >
                          {chat.subtitle}
                        </Text>
                      ) : null}
                    </View>
                  </>
                );
                // Only interactive when the host wired a navigation handler —
                // otherwise render a static row so it doesn't look tappable-but-dead.
                return chat.onPress !== undefined ? (
                  <Pressable
                    key={chat.id}
                    accessibilityRole="button"
                    accessibilityLabel={chat.name}
                    onPress={chat.onPress}
                    className="flex-row items-center gap-2 p-1 active:opacity-70"
                  >
                    {inner}
                  </Pressable>
                ) : (
                  <View key={chat.id} className="flex-row items-center gap-2 p-1">
                    {inner}
                  </View>
                );
              })}
            </View>
          </View>
        ) : null}
      </View>
      </ScrollView>

      {moreOpen && moreMenu !== undefined ? (
        <>
          <Pressable
            className="absolute inset-0"
            accessibilityRole="button"
            accessibilityLabel="Dismiss menu"
            onPress={() => setMoreOpen(false)}
          />
          <View style={{ position: 'absolute', top: 56, right: 16 }}>
            {/* "View full profile" is intentionally omitted here — this IS the
                full profile modal, so it would be a redundant no-op. */}
            <ProfileMoreMenu
              groups={moreMenu.groups}
              onAddToGroup={(id) => {
                setMoreOpen(false);
                moreMenu.onAddToGroup(id);
              }}
              onBlock={() => {
                setMoreOpen(false);
                moreMenu.onBlock();
              }}
            />
          </View>
        </>
      ) : null}
    </View>
  );
}
